import { HttpErrorResponse } from '@angular/common/http';
import { AppError, createAppError, isAppError } from './app-error.model';

/**
 * Stable frontend-facing error codes.
 *
 * These codes are kept internal to the application and exist so presentation,
 * logging, and future test coverage can rely on deterministic identifiers instead
 * of backend wording or ad hoc component strings.
 */
export const APP_ERROR_CODES = {
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_TOKEN_MISSING: 'AUTH_TOKEN_MISSING',
  NETWORK_UNAVAILABLE: 'NETWORK_UNAVAILABLE',
  REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
  HTTP_UNAUTHORIZED: 'HTTP_UNAUTHORIZED',
  HTTP_FORBIDDEN: 'HTTP_FORBIDDEN',
  HTTP_NOT_FOUND: 'HTTP_NOT_FOUND',
  HTTP_CONFLICT: 'HTTP_CONFLICT',
  HTTP_UNPROCESSABLE: 'HTTP_UNPROCESSABLE',
  HTTP_SERVER_ERROR: 'HTTP_SERVER_ERROR',
  HTTP_GENERIC_ERROR: 'HTTP_GENERIC_ERROR',
  UNEXPECTED_RUNTIME_ERROR: 'UNEXPECTED_RUNTIME_ERROR',
  PREDICTION_REJECTED: 'PREDICTION_REJECTED',
  PREDICTION_REQUEST_BUILD_FAILED: 'PREDICTION_REQUEST_BUILD_FAILED',
  REVISED_SHEARED_KV100_INVALID_INPUT: 'REVISED_SHEARED_KV100_INVALID_INPUT'
} as const;

const USER_MESSAGES: Record<string, string> = {
  [APP_ERROR_CODES.AUTH_REQUIRED]: 'Your session is not active. Please sign in again.',
  [APP_ERROR_CODES.AUTH_TOKEN_MISSING]: 'We could not validate your session. Please sign in again.',
  [APP_ERROR_CODES.NETWORK_UNAVAILABLE]: 'The service is currently unavailable. Please try again shortly.',
  [APP_ERROR_CODES.REQUEST_TIMEOUT]: 'The request took too long. Please try again.',
  [APP_ERROR_CODES.HTTP_UNAUTHORIZED]: 'Your session is not active. Please sign in again.',
  [APP_ERROR_CODES.HTTP_FORBIDDEN]: 'You do not have access to complete this action.',
  [APP_ERROR_CODES.HTTP_NOT_FOUND]: 'The requested resource could not be found.',
  [APP_ERROR_CODES.HTTP_CONFLICT]: 'The request could not be completed because the data is out of date.',
  [APP_ERROR_CODES.HTTP_UNPROCESSABLE]: 'The request could not be processed with the current data.',
  [APP_ERROR_CODES.HTTP_SERVER_ERROR]: 'The service is temporarily unavailable. Please try again later.',
  [APP_ERROR_CODES.HTTP_GENERIC_ERROR]: 'The request could not be completed at this time.',
  [APP_ERROR_CODES.UNEXPECTED_RUNTIME_ERROR]: 'Something unexpected happened. Please try again.',
  [APP_ERROR_CODES.PREDICTION_REJECTED]: 'Prediction could not be generated. Review Model Inputs.',
  [APP_ERROR_CODES.PREDICTION_REQUEST_BUILD_FAILED]: 'The prediction request could not be prepared.',
  [APP_ERROR_CODES.REVISED_SHEARED_KV100_INVALID_INPUT]: 'Revised sheared KV100 values could not be calculated. Check VM treatments and KV100 inputs.'
};

/**
 * Returns the product-safe message associated with an internal error code.
 */
export function getUserMessage(code: string): string {
  return USER_MESSAGES[code] ?? USER_MESSAGES[APP_ERROR_CODES.UNEXPECTED_RUNTIME_ERROR];
}

/**
 * Derives a compact source identifier from a request URL.
 * This keeps logs readable even when the caller only receives an HttpErrorResponse.
 */
export function deriveSourceFromUrl(url: string | null | undefined): string {
  if (!url) {
    return 'unknown-http-source';
  }

  try {
    const parsedUrl = new URL(url, 'http://placeholder');
    const segments = parsedUrl.pathname.split('/').filter(Boolean);
    return segments.at(-1) ?? 'unknown-http-source';
  } catch {
    const segments = url.split('/').filter(Boolean);
    return segments.at(-1) ?? 'unknown-http-source';
  }
}

/**
 * Maps transport-level HTTP failures into the shared AppError contract.
 * The user-visible message is intentionally generic and never forwards raw server text.
 */
export function mapHttpError(error: HttpErrorResponse): AppError {
  const source = deriveSourceFromUrl(error.url);
  const technicalMessage = typeof error.error === 'string'
    ? error.error
    : error.message;

  if (error.status === 0) {
    return createAppError({
      category: 'design',
      code: APP_ERROR_CODES.NETWORK_UNAVAILABLE,
      source,
      userMessage: getUserMessage(APP_ERROR_CODES.NETWORK_UNAVAILABLE),
      technicalMessage,
      httpStatus: error.status,
      endpoint: error.url ?? undefined,
      originalError: error
    });
  }

  const statusMap: Record<number, string> = {
    401: APP_ERROR_CODES.HTTP_UNAUTHORIZED,
    403: APP_ERROR_CODES.HTTP_FORBIDDEN,
    404: APP_ERROR_CODES.HTTP_NOT_FOUND,
    409: APP_ERROR_CODES.HTTP_CONFLICT,
    422: APP_ERROR_CODES.HTTP_UNPROCESSABLE
  };

  const code = error.status >= 500
    ? APP_ERROR_CODES.HTTP_SERVER_ERROR
    : (statusMap[error.status] ?? APP_ERROR_CODES.HTTP_GENERIC_ERROR);

  return createAppError({
    category: 'design',
    code,
    source,
    userMessage: getUserMessage(code),
    technicalMessage,
    httpStatus: error.status,
    endpoint: error.url ?? undefined,
    originalError: error
  });
}

/**
 * Normalizes any unknown failure into AppError.
 * Already-normalized AppError instances are preserved as-is so callers can layer
 * context without losing the original classification.
 */
export function mapUnknownError(error: unknown, source: string): AppError {
  if (isAppError(error)) {
    return error;
  }

  if (error instanceof HttpErrorResponse) {
    return mapHttpError(error);
  }

  const technicalMessage = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : 'Unknown error';

  return createAppError({
    category: 'unclassified',
    code: APP_ERROR_CODES.UNEXPECTED_RUNTIME_ERROR,
    source,
    userMessage: getUserMessage(APP_ERROR_CODES.UNEXPECTED_RUNTIME_ERROR),
    technicalMessage,
    originalError: error
  });
}